<script setup>
import { storeToRefs } from 'pinia'
import { useRouteQuery } from '@vueuse/router'
import IconSearch from 'assets/icons/search.svg?component'
import IconCoupon from 'assets/icons/coupon.svg?raw'
import IconLoyalty from 'assets/icons/loyalty.svg?raw'
import Error from 'assets/icons/error.svg?component'
import { useMessageStore } from '~/stores/messages'
import SkeletonPlaceholder from '@/components/Base/SkeletonPlaceholder'

// Get route and router
const router = useRouter()
const route = useRoute()
const centerRoute = computed(() => route.params?.centerUid)

// Fetch messages
const messagesStore = useMessageStore()
const { messages: staticKeys } = storeToRefs(messagesStore)

// Fetch shop data
const { data: shops, isPlaceholderData: isShopListPlaceholderData, suspense: shopListSuspense } = useFetchShopList(centerRoute.value)

// Prefetch data on the server side
onServerPrefetch(async () => {
    await shopListSuspense() // Wait for the query to finish fetching
})

// Define initial refs
const query = ref('')

// Get query values
const hasCouponsQuery = useRouteQuery('coupons', 0, { mode: 'push' })
const hasLoyaltyProgramQuery = useRouteQuery('loyalty', 0, { mode: 'push' })
const shopGastroQuery = useRouteQuery('type', '', { mode: 'push' })

const hasCoupons = computed({
    get: () => hasCouponsQuery.value,
    set(value) {
        if (hasCouponsQuery.value) {
            const existingQuery = { ...route.query }
            delete existingQuery.coupons

            router.push({
                path: route.path,
                query: existingQuery
            })
        } else {
            hasCouponsQuery.value = value
        }
    }
})

const hasLoyaltyProgram = computed({
    get: () => hasLoyaltyProgramQuery.value,
    set(value) {
        if (hasLoyaltyProgramQuery.value) {
            const existingQuery = { ...route.query }
            delete existingQuery.loyalty

            router.push({
                path: route.path,
                query: existingQuery
            })
        } else {
            hasLoyaltyProgramQuery.value = value
        }
    }
})

const shopGastro = computed({
    get: () => shopGastroQuery.value,
    set(value) {
        shopGastroQuery.value = value
    }
})

/**
 * Deselects a radio button if its value matches the current shopGastroQuery value.
 *
 * @param {any} value - The value of the radio button to check against shopGastroQuery.
 */
const deselectRadio = (value) => {
    if (shopGastroQuery.value === value) {
        const existingQuery = { ...route.query }
        delete existingQuery.type

        router.push({
            path: route.path,
            query: existingQuery
        })
    }
}

/**
 * Clears the search query.
 *
 */
const clearSearch = () => {
    query.value = ''
}

// Filter config
const filterConfigs = reactive([
    {
        id: 'coupons',
        type: 'checkbox',
        name: 'coupons',
        labelKey: staticKeys.value?.shops_overview_filter_coupons,
        icon: IconCoupon,
        model: hasCoupons,
        active: hasCoupons
    },
    {
        id: 'loyalty',
        type: 'checkbox',
        name: 'loyalty',
        labelKey: staticKeys.value?.shops_overview_filter_loyalty,
        icon: IconLoyalty,
        model: hasLoyaltyProgram,
        active: hasLoyaltyProgram
    },
    {
        id: 'shop',
        type: 'radio',
        name: 'shopGastro',
        labelKey: staticKeys.value?.shops_overview_filter_shops,
        model: shopGastro,
        value: 'shop',
        active: shopGastro.value === 'shop',
        clickHandler: deselectRadio
    },
    {
        id: 'gastro',
        type: 'radio',
        name: 'shopGastro',
        labelKey: staticKeys.value?.shops_overview_filter_gastro,
        model: shopGastro,
        value: 'gastro',
        active: shopGastro.value === 'gastro',
        clickHandler: deselectRadio
    }
])

// Define filtered shops
const filteredShops = computed(() => {
    if (!shops.value || typeof shops.value !== 'object') {
        return {}
    }

    return Object.keys(shops.value).reduce((acc, key) => {
        const shopsWithLoyalty = shops.value[key].filter(shop => shop.hasLoyaltyProgram)
        const shopsWithoutLoyalty = shops.value[key].filter(shop => !shop.hasLoyaltyProgram)

        const filteredArrayWithLoyalty = shopsWithLoyalty.filter((shop) => {
            const shopNameMatch = shop.name?.toLowerCase().includes(query.value?.toLowerCase())
            const shopCouponsMatch = hasCoupons.value ? shop.hasCoupons === true : true
            const shopLoyaltyMatch = hasLoyaltyProgram.value ? shop.hasLoyaltyProgram === true : true
            const shopGastroMatch = shopGastro.value ? shop.type === shopGastro.value : true

            return shopNameMatch && shopLoyaltyMatch && shopCouponsMatch && shopGastroMatch
        })

        const filteredArrayWithoutLoyalty = shopsWithoutLoyalty.filter((shop) => {
            const shopNameMatch = shop.name?.toLowerCase().includes(query.value?.toLowerCase())
            const shopCouponsMatch = hasCoupons.value ? shop.hasCoupons === true : true
            const shopGastroMatch = shopGastro.value ? shop.type === shopGastro.value : true

            return shopNameMatch && shopCouponsMatch && shopGastroMatch
        })

        const filteredArray = hasLoyaltyProgram.value
            ? filteredArrayWithLoyalty
            : [
                    ...filteredArrayWithLoyalty,
                    ...filteredArrayWithoutLoyalty
                ]

        if (filteredArray.length > 0) {
            acc[key] = filteredArray
        }

        return acc
    }, {})
})

// Check if there are any shops
const hasShops = computed(() => filteredShops.value ? Object.keys(filteredShops.value).length > 0 : false)

watch(
    () => filteredShops.value, () => {
        if (import.meta.client) {
            if (!hasShops.value) {
                window.scrollTo({
                    top: 0
                })
            }
        }
    }
)
</script>

<template>
    <div class="shop-list-actions">
        <component
            :is="SkeletonPlaceholder"
            v-if="isShopListPlaceholderData"
            :skeleton-placeholder-config="{ type: 'multi-headline', tag: 'h1' }"
        />
        <label v-else class="shop-list-search">
            <IconSearch class="shop-list-search-icon" />

            <input
                v-model="query"
                type="text"
                :placeholder="staticKeys?.shops_overview_search_label"
            >
            <Error class="shop-list-search-clear" @click="clearSearch" />
        </label>

        <FilterList>
            <template
                v-for="(filterConfig, index) in filterConfigs"
                :key="`filter-${index}`"
            >
                <component
                    :is="SkeletonPlaceholder"
                    v-if="isShopListPlaceholderData"
                    :skeleton-placeholder-config="{ type: 'button' }"
                />

                <FilterListItem
                    v-else
                    :class="{ 'filter-item-active': filterConfig.active }"
                >
                    <input
                        :id="filterConfig.id"
                        v-model="filterConfig.model"
                        :value="filterConfig.value"
                        :type="filterConfig.type"
                        :name="filterConfig.name"
                        @click="filterConfig?.clickHandler ? filterConfig?.clickHandler(filterConfig.value) : null"
                    >

                    <label :for="filterConfig.id">
                        <span
                            v-if="filterConfig.icon"
                            class="filter-item-icon"
                            v-html="filterConfig.icon"
                        />

                        <span>{{ filterConfig.labelKey }}</span>
                    </label>
                </FilterListItem>
            </template>
        </FilterList>
    </div>

    <ShopList :shops="{ ...filteredShops, placeholder: isShopListPlaceholderData }" />
</template>

 <style lang="scss">
 .shop-list-actions {
     position: sticky;
     z-index: z('shop-list-actions');
     top: px2rem(-1);
     padding-block: px2rem(20);
     background: $shop-list-actions-bg;
 }

 .shop-list-search-clear {
     width: px2rem(22);
     height: px2rem(22);
     opacity: 0;
     pointer-events: none;
     transition: opacity 400ms ease;
 }

 .shop-list-search {
     margin-block-end: px2rem(42);

     @include searchfield;

     input:not(:placeholder-shown) ~ .shop-list-search-clear {
         opacity: 1;
         pointer-events: all;
         cursor: pointer;
     }
 }

 .shop-list-search-icon {
     width: px2rem(18);
     height: auto;
     color: $shop-search-icon;
     margin-inline-end: px2rem(10);
 }
</style>
